import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTasks } from '../../hooks/useTasks';
import Header from './Header';
import TaskForm from './TaskForm';
import TaskList from './TaskList';
import TaskStats from './TaskStats';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const { tasks, loading, createTask, updateTask, deleteTask, fetchTasks } = useTasks();
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, completed
  const [sortBy, setSortBy] = useState('created_at'); // created_at, due_date, priority

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCreateTask = async (taskData) => {
    const success = await createTask(taskData);
    if (success) {
      setShowTaskForm(false);
    }
  };

  const handleUpdateTask = async (taskId, taskData) => {
    const success = await updateTask(taskId, taskData);
    if (success) {
      setEditingTask(null);
    }
  };

  const handleDeleteTask = async (taskId) => {
    await deleteTask(taskId);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowTaskForm(true);
  };

  const handleCloseForm = () => {
    setShowTaskForm(false);
    setEditingTask(null);
  };

  const filteredTasks = tasks.filter(task => {
    switch (filter) {
      case 'active':
        return !task.completed;
      case 'completed':
        return task.completed;
      default:
        return true;
    }
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    switch (sortBy) {
      case 'due_date':
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="bamboo-loader">
          <div className="bamboo-segment"></div>
          <div className="bamboo-segment"></div>
          <div className="bamboo-segment"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Header 
        user={user}
        onNewTask={() => setShowTaskForm(true)}
      />
      
      <main className="dashboard-main">
        <div className="container">
          <div className="dashboard-grid">
            <div className="dashboard-sidebar">
              <TaskStats tasks={tasks} />
              
              <div className="filters-card card">
                <h3>Filter Tasks</h3>
                <div className="filter-buttons">
                  <button
                    className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('all')}
                  >
                    All Tasks
                  </button>
                  <button
                    className={`btn ${filter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('active')}
                  >
                    Active
                  </button>
                  <button
                    className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFilter('completed')}
                  >
                    Completed
                  </button>
                </div>
                
                <div className="sort-section">
                  <h4>Sort By</h4>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="input"
                  >
                    <option value="created_at">Date Created</option>
                    <option value="due_date">Due Date</option>
                    <option value="priority">Priority</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="dashboard-content">
              <TaskList
                tasks={sortedTasks}
                onEdit={handleEditTask}
                onDelete={handleDeleteTask}
                onToggleComplete={handleUpdateTask}
              />
            </div>
          </div>
        </div>
      </main>

      {showTaskForm && (
        <TaskForm
          task={editingTask}
          onSubmit={editingTask ? 
            (data) => handleUpdateTask(editingTask.id, data) : 
            handleCreateTask
          }
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default Dashboard;
